import React, { useState, useEffect, useRef } from 'react';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { useTranslation } from 'react-i18next';
import MobileBottomSheet from './MobileBottomSheet';

// URL du backend (pour les images et les liens de partage avec Open Graph)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// URL du frontend
const getFrontendUrl = () => {
  return window.location.origin;
};

export default function TrackContextMenu({ 
  track, 
  isOpen, 
  onClose, 
  position,
  playlists,
  folders,
  onAddToPlaylist,
  onToggleLike,
  isLiked,
  currentPlaylistId,
  onRemoveFromPlaylist,
  onShowToast,
  userIsPremium = false,
  user,
  onOpenSubscription
}) {
  const NativeShare = registerPlugin('NativeShare');
  const { t } = useTranslation();
  const [showPlaylistSubmenu, setShowPlaylistSubmenu] = useState(false);
  const [showShareSubmenu, setShowShareSubmenu] = useState(false);
  const [showPremiumDownloadModal, setShowPremiumDownloadModal] = useState(false);
  const [shareToast, setShareToast] = useState('');
  const [shareMenuStyle, setShareMenuStyle] = useState({ top: 0, left: 0 });
  const shareToastTimerRef = useRef(null);
  const menuRef = useRef(null);
  const submenuRef = useRef(null);
  const shareSubmenuRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // Si on est dans une playlist, ne pas afficher le sous-menu
  const isInPlaylist = currentPlaylistId !== undefined && currentPlaylistId !== null;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) return;
    const handleClickOutside = (event) => {
      // Vérifier si le clic est en dehors de tous les menus
      const isOutsideMenu = !menuRef.current || !menuRef.current.contains(event.target);
      const isOutsideSubmenu = !submenuRef.current || !submenuRef.current.contains(event.target);
      const isOutsideShareSubmenu = !shareSubmenuRef.current || !shareSubmenuRef.current.contains(event.target);
      
      if (isOutsideMenu && isOutsideSubmenu && isOutsideShareSubmenu) {
        setShowPlaylistSubmenu(false);
        setShowShareSubmenu(false);
        onClose();
      }
    };

    if (isOpen) {
      // Ajouter un petit délai pour éviter la fermeture immédiate
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
      }, 100);
      
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }
  }, [isOpen, onClose, isMobile]);

  // Reset submenus when menu closes
  useEffect(() => {
    if (!isOpen) {
      setShowPlaylistSubmenu(false);
      setShowShareSubmenu(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!showShareSubmenu) return;
    const menu = shareSubmenuRef.current;
    if (!menu) return;
    const padding = 12;
    const width = menu.offsetWidth || 220;
    const height = menu.offsetHeight || 120;
    let top = position.y + 80;
    let left = position.x - 190;
    const maxLeft = Math.max(padding, window.innerWidth - width - padding);
    const maxTop = Math.max(padding, window.innerHeight - height - padding);
    left = Math.min(Math.max(padding, left), maxLeft);
    top = Math.min(Math.max(padding, top), maxTop);
    setShareMenuStyle({ top, left });
  }, [showShareSubmenu, position.x, position.y]);

  // Organiser les playlists par dossier
  const playlistsByFolder = {
    null: playlists?.filter(p => !p.folder_id) || []
  };
  
  folders?.forEach(folder => {
    playlistsByFolder[folder.id] = playlists?.filter(p => p.folder_id === folder.id) || [];
  });

  // Générer l'URL d'embed et de partage
  // Pour le partage social (WhatsApp, Facebook, etc.), utiliser le backend qui sert les meta tags Open Graph
  // Pour l'embed, utiliser le frontend directement
  const frontendUrl = getFrontendUrl();
  const origin = typeof window !== 'undefined' ? window.location.origin.replace(/\/+$/, '') : '';
  const safeOrigin = origin && !origin.startsWith('capacitor://') ? origin : '';
  const shareBaseRaw = (process.env.REACT_APP_SHARE_BASE || BACKEND_URL || safeOrigin || '').replace(/\/+$/, '');
  const shareBase = shareBaseRaw;
  const shareUrl = shareBase ? `${shareBase}/api/share/track/${track?.id}` : ''; // Backend avec Open Graph meta tags
  const embedUrl = `${frontendUrl}/#/embed/track/${track?.id}`; // Frontend pour l'iframe
  
  // Générer le code iframe
  const iframeCode = `<iframe src="${embedUrl}" width="100%" height="152" frameborder="0" allowtransparency="true" allow="encrypted-media; autoplay; clipboard-write"></iframe>`;

  // Fonction pour copier le code iframe
  const handleCopyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(iframeCode);
      setShareToast(t('share.embedCopied') || "Code d'intégration copié");
      if (shareToastTimerRef.current) clearTimeout(shareToastTimerRef.current);
      shareToastTimerRef.current = setTimeout(() => setShareToast(''), 1500);
      onClose();
    } catch (err) {
      console.error('Erreur lors de la copie:', err);
      onClose();
    }
  };

  // Fonction pour partager sur les réseaux sociaux
  const handleShare = async () => {
    if (!shareUrl) return;
    try {
      const safeShareUrl = /^https?:\/\//i.test(shareUrl) ? shareUrl : '';
      const sharePayload = {
        title: track?.title || t('share.title') || 'Partager',
        text: track?.artist ? `${track.title} • ${track.artist}` : track?.title,
        ...(safeShareUrl ? { url: safeShareUrl } : {})
      };
      if (!isMobile) {
        await navigator.clipboard.writeText(shareUrl);
        setShareToast(t('share.linkCopied') || 'Lien de partage copié');
        if (shareToastTimerRef.current) clearTimeout(shareToastTimerRef.current);
        shareToastTimerRef.current = setTimeout(() => setShareToast(''), 1500);
        onClose();
        return;
      }
      const platform = Capacitor?.getPlatform?.() || 'web';
      const isNative = platform !== 'web';
      const canNativeShare = platform === 'ios' && Capacitor?.isPluginAvailable?.('NativeShare');
      if (canNativeShare) {
        await NativeShare.share(sharePayload);
        onClose();
        return;
      }
      const canCapShare = !!(isNative && Capacitor?.isPluginAvailable?.('Share'));
      if (canCapShare) {
        await Share.share(sharePayload);
        onClose();
        return;
      }
      if (isNative) {
        if (onShowToast) {
          onShowToast(t('share.unavailable') || 'Partage indisponible sur cet appareil');
        }
        onClose();
        return;
      }
      if (navigator.share) {
        await navigator.share(sharePayload);
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setShareToast(t('share.linkCopied') || 'Lien de partage copié');
        if (shareToastTimerRef.current) clearTimeout(shareToastTimerRef.current);
        shareToastTimerRef.current = setTimeout(() => setShareToast(''), 1500);
      }
    } catch (err) {
      console.error('Erreur clipboard:', err);
    }
    onClose();
  };

  useEffect(() => {
    return () => {
      if (shareToastTimerRef.current) {
        clearTimeout(shareToastTimerRef.current);
      }
    };
  }, []);

  if (!isOpen && !showPremiumDownloadModal && !shareToast && !showPlaylistSubmenu && !showShareSubmenu) return null;

  const handleCloseAll = () => {
    setShowPlaylistSubmenu(false);
    setShowShareSubmenu(false);
    onClose();
  };

  // Fonction pour télécharger la piste
  const handleDownload = async () => {
    // Vérifier si l'utilisateur est connecté
    if (!user) {
      if (onShowToast) {
        onShowToast(t('auth.loginRequired') || 'Veuillez vous connecter pour télécharger');
      }
      onClose();
      return;
    }

    // Vérifier si la piste est premium et si l'utilisateur n'est pas premium
    const canDownload = !track?.is_premium || userIsPremium || user?.status === 'ADMIN';
    
    if (!canDownload) {
      // Afficher la modale premium
      setShowPremiumDownloadModal(true);
      onClose();
      return;
    }

    // Télécharger la piste
    try {
      if (onShowToast) {
        onShowToast(t('download.starting') || 'Téléchargement en cours...');
      }

      const audioUrl = track.file_path;
      const response = await fetch(audioUrl);
      const blob = await response.blob();
      
      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      
      // Nom du fichier : titre - artiste.mp3
      const fileName = `${track.title} - ${track.artist || 'Unknown'}.mp3`;
      a.download = fileName;
      
      document.body.appendChild(a);
      a.click();
      
      // Nettoyer
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      if (onShowToast) {
        onShowToast(t('download.complete') || 'Téléchargement terminé !');
      }
    } catch (err) {
      console.error('Erreur de téléchargement:', err);
      if (onShowToast) {
        onShowToast(t('download.error') || 'Erreur lors du téléchargement');
      }
    }
    onClose();
  };

  // Naviguer vers la page d'abonnement
  const handleGoToSubscription = () => {
    setShowPremiumDownloadModal(false);
    if (onOpenSubscription) {
      onOpenSubscription();
    }
  };

  const mainMenuContent = (
    <div className={isMobile ? "w-full" : "bg-[#2a2a2f] rounded-xl shadow-2xl border border-white/10 py-2 min-w-[280px] max-w-[90vw]"}>
      <div className="px-4 py-3 border-b border-white/10">
        <div className="font-semibold text-sm truncate">{track?.title}</div>
        <div className="text-xs text-white/60 truncate">{track?.artist || track?.artist_name || ''}</div>
      </div>
      <div className="py-1">
        {/* Like / Unlike */}
        <button
          onClick={() => {
            onToggleLike(track.id);
            handleCloseAll();
          }}
          className="w-full px-4 py-2 hover:bg-white/10 text-left flex items-center gap-3"
        >
          <svg className="w-5 h-5" fill={isLiked ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
          <span>{isLiked ? t('track.removeFromLiked') : t('track.addToLiked')}</span>
        </button>

        {/* Ajouter à la playlist */}
        {!isInPlaylist ? (
          <button
            onClick={() => {
              setShowShareSubmenu(false);
              if (isMobile) {
                setShowPlaylistSubmenu(true);
              } else {
                setShowPlaylistSubmenu(!showPlaylistSubmenu);
              }
            }}
            className="w-full px-4 py-2 hover:bg-white/10 text-left flex items-center justify-between gap-3"
          >
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5" fill="white" viewBox="0 0 24 24">
                <path d="M15 6H3v2h12V6zm0 4H3v2h12v-2zM3 16h8v-2H3v2zM17 6v8.18c-.31-.11-.65-.18-1-.18-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3V8h3V6h-5z"/>
              </svg>
              <span>{t('track.addToPlaylist')}</span>
            </div>
            {!isMobile && (
              <svg className="w-4 h-4" fill="white" viewBox="0 0 24 24">
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
              </svg>
            )}
          </button>
        ) : (
          <button
            onClick={() => {
              setShowShareSubmenu(false);
              setShowPlaylistSubmenu(!showPlaylistSubmenu);
            }}
            className="w-full px-4 py-2 hover:bg-white/10 text-left flex items-center gap-3"
          >
            <svg className="w-5 h-5" fill="white" viewBox="0 0 24 24">
              <path d="M15 6H3v2h12V6zm0 4H3v2h12v-2zM3 16h8v-2H3v2zM17 6v8.18c-.31-.11-.65-.18-1-.18-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3V8h3V6h-5z"/>
            </svg>
            <span>{t('playlist.removeTrack')}</span>
          </button>
        )}

        {/* Télécharger */}
        <button
          onClick={handleDownload}
          className="w-full px-4 py-2 hover:bg-white/10 text-left flex items-center gap-3"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          <span>{t('download.download') || 'Télécharger'}</span>
          {track?.is_premium && !userIsPremium && user?.status !== 'ADMIN' && (
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-yellow-500 ml-auto">
              <path d="M5 16L3 5L8.5 10L12 4L15.5 10L21 5L19 16H5M19 19C19 19.6 18.6 20 18 20H6C5.4 20 5 19.6 5 19V18H19V19Z"/>
            </svg>
          )}
        </button>

        {/* Partager */}
        <button
          onClick={() => {
            setShowPlaylistSubmenu(false);
            if (isMobile) {
              handleShare();
            } else {
              setShowShareSubmenu(!showShareSubmenu);
            }
          }}
          className="w-full px-4 py-2 hover:bg-white/10 text-left flex items-center justify-between gap-3"
        >
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
            <span>{t('share.title') || 'Partager'}</span>
          </div>
          {!isMobile && (
            <svg className="w-4 h-4 rotate-180" fill="white" viewBox="0 0 24 24">
              <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
            </svg>
          )}
        </button>
      </div>
    </div>
  );

  const playlistSubmenuContent = (
    <div className={isMobile ? "w-full" : "bg-[#2a2a2f] rounded-xl shadow-2xl border border-white/10 py-2 min-w-[280px] max-w-[90vw]"}>
      <div className="px-4 py-2 border-b border-white/10 font-semibold text-sm">
        {t('track.addToPlaylist')}
      </div>
      <div className="max-h-[50vh] overflow-y-auto">
        {/* Playlists sans dossier */}
        {playlistsByFolder[null]?.length > 0 && (
          <div>
            {playlistsByFolder[null].map(playlist => (
              <button
                key={playlist.id}
                onClick={() => {
                  onAddToPlaylist(track.id, playlist.id);
                  handleCloseAll();
                }}
                className="w-full px-4 py-2 hover:bg-white/10 text-left flex items-center gap-3"
              >
                <svg className="w-5 h-5" fill="white" viewBox="0 0 24 24">
                  <path d="M15 6H3v2h12V6zm0 4H3v2h12v-2zM3 16h8v-2H3v2zM17 6v8.18c-.31-.11-.65-.18-1-.18-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3V8h3V6h-5z"/>
                </svg>
                <div className="flex-1 min-w-0">
                  <div className="truncate text-sm">{playlist.name}</div>
                  <div className="text-xs text-white/60">{playlist.track_count || 0} {t('track.tracksCount')}</div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Playlists par dossier */}
        {folders?.map(folder => {
          const folderPlaylists = playlistsByFolder[folder.id];
          if (!folderPlaylists || folderPlaylists.length === 0) return null;

          return (
            <div key={folder.id} className="border-t border-white/10">
              <div className="px-4 py-2 text-xs text-white/60 font-semibold flex items-center gap-2">
                <svg className="w-4 h-4" fill="white" viewBox="0 0 24 24">
                  <path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z"/>
                </svg>
                {folder.name}
              </div>
              {folderPlaylists.map(playlist => (
                <button
                  key={playlist.id}
                  onClick={() => {
                    onAddToPlaylist(track.id, playlist.id);
                    handleCloseAll();
                  }}
                  className="w-full px-6 py-2 hover:bg-white/10 text-left flex items-center gap-3"
                >
                  <svg className="w-4 h-4" fill="white" viewBox="0 0 24 24">
                    <path d="M15 6H3v2h12V6zm0 4H3v2h12v-2zM3 16h8v-2H3v2zM17 6v8.18c-.31-.11-.65-.18-1-.18-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3V8h3V6h-5z"/>
                  </svg>
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-sm">{playlist.name}</div>
                    <div className="text-xs text-white/60">{playlist.track_count || 0} {t('track.tracksCount')}</div>
                  </div>
                </button>
              ))}
            </div>
          );
        })}

        {(!playlists || playlists.length === 0) && (
          <div className="px-4 py-8 text-center text-white/60 text-sm">
            {t('playlist.noPlaylist')}
          </div>
        )}
      </div>
    </div>
  );

  const shareSubmenuContent = (
    <div className={isMobile ? "w-full" : "bg-[#2a2a2f] rounded-xl shadow-2xl border border-white/10 py-2 min-w-[280px] max-w-[90vw]"}>
      <div className="px-4 py-2 border-b border-white/10 font-semibold text-sm">
        {t('share.title') || 'Partager'}
      </div>
      <button
        onClick={handleShare}
        className="w-full px-4 py-3 hover:bg-white/10 text-left flex items-center gap-3"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
        <span>{t('share.share') || 'Partager'}</span>
      </button>
      {!isMobile && (
        <button
          onClick={handleCopyEmbed}
          className="w-full px-4 py-3 hover:bg-white/10 text-left flex items-center gap-3"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
          </svg>
          <span>{t('share.embed') || 'Intégrer'}</span>
        </button>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <MobileBottomSheet
        isOpen={isOpen || showPlaylistSubmenu || showShareSubmenu || showPremiumDownloadModal}
        onClose={handleCloseAll}
      >
        {showShareSubmenu ? shareSubmenuContent : showPlaylistSubmenu ? playlistSubmenuContent : mainMenuContent}
      </MobileBottomSheet>
    );
  }

  return (
    <>
      {/* Modal Premium pour téléchargement */}
      {showPremiumDownloadModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 modal-text">
          {/* Overlay */}
          <div 
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowPremiumDownloadModal(false)}
          />
          
          {/* Modal */}
          <div className="relative bg-gradient-to-br from-zinc-900 to-black rounded-2xl shadow-2xl max-w-md w-full p-8 border border-yellow-500/30 animate-scale-in">
            {/* Crown Icon */}
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-yellow-500/20 to-yellow-600/20 flex items-center justify-center">
              <svg 
                viewBox="0 0 24 24" 
                fill="currentColor" 
                className="w-10 h-10 text-yellow-500"
                style={{ filter: 'drop-shadow(0 0 2px rgba(234, 179, 8, 0.5))' }}
              >
                <path d="M5 16L3 5L8.5 10L12 4L15.5 10L21 5L19 16H5M19 19C19 19.6 18.6 20 18 20H6C5.4 20 5 19.6 5 19V18H19V19Z"/>
              </svg>
            </div>
            
            {/* Title */}
            <h3 className="text-2xl font-bold text-white text-center mb-3">
              {t('download.premiumRequired') || 'Téléchargement Premium'}
            </h3>
            
            {/* Track info */}
            {track && (
              <div className="flex items-center gap-3 bg-white/5 rounded-lg p-3 mb-4">
                {track.cover_image ? (
                  <img 
                    src={track.cover_image.startsWith('http') ? track.cover_image : `${BACKEND_URL}${track.cover_image}`}
                    alt={track.title}
                    className="w-12 h-12 rounded object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-xl">
                    {track.cover_emoji || '🎵'}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-white truncate">{track.title}</div>
                  <div className="text-sm text-white/60 truncate">{track.artist}</div>
                </div>
              </div>
            )}
            
            {/* Message */}
            <p className="text-white/70 text-center mb-6">
              {t('download.premiumMessage') || 'Le téléchargement de cette piste est réservé aux abonnés Premium. Passez à Premium pour télécharger toutes vos musiques préférées.'}
            </p>
            
            {/* Benefits */}
            <div className="space-y-2 mb-6">
              <div className="flex items-center gap-2 text-sm text-white/80">
                <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span>{t('download.benefit1') || 'Téléchargement illimité'}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-white/80">
                <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span>{t('download.benefit2') || 'Qualité audio maximale'}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-white/80">
                <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span>{t('download.benefit3') || 'Écoute hors-ligne'}</span>
              </div>
            </div>
            
            {/* Buttons */}
            <div className="flex flex-col gap-3">
              <button
                onClick={handleGoToSubscription}
                className="w-full px-6 py-3 rounded-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500 text-black font-bold transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
              >
                <svg 
                  viewBox="0 0 24 24" 
                  fill="currentColor" 
                  className="w-5 h-5"
                >
                  <path d="M5 16L3 5L8.5 10L12 4L15.5 10L21 5L19 16H5M19 19C19 19.6 18.6 20 18 20H6C5.4 20 5 19.6 5 19V18H19V19Z"/>
                </svg>
                {t('download.seePlans') || 'Voir les abonnements'}
              </button>
              <button
                onClick={() => setShowPremiumDownloadModal(false)}
                className="w-full px-6 py-3 rounded-full bg-white/10 hover:bg-white/20 text-white font-medium transition-colors"
              >
                {t('common.later') || 'Plus tard'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isOpen && (
        <>
          {/* Overlay transparent pour fermer le menu au clic */}
          <div 
            className="fixed inset-0 z-[49]"
            onClick={onClose}
            onTouchStart={onClose}
          />
          
          {/* Menu principal */}
          <div
            ref={menuRef}
            className="fixed bg-zinc-800 rounded-lg shadow-2xl border border-white/10 py-1 min-w-[200px] z-50"
            style={{
              top: position.y,
              left: position.x,
            }}
          >
            {/* Like/Unlike */}
            <button
              onClick={() => {
                onToggleLike(track.id);
                onClose();
              }}
              className="w-full px-4 py-2 hover:bg-white/10 text-left flex items-center gap-3"
            >
              <svg className="w-5 h-5" fill={isLiked ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              <span>{isLiked ? t('track.removeFromLiked') : t('track.addToLiked')}</span>
            </button>

            {/* Add to playlist / Remove from playlist */}
            {isInPlaylist ? (
              <button
                onClick={() => {
                  onRemoveFromPlaylist(track.id, currentPlaylistId);
                  onClose();
                }}
                className="w-full px-4 py-2 hover:bg-white/10 text-left flex items-center gap-3"
              >
                <svg className="w-5 h-5" fill="white" viewBox="0 0 24 24">
                  <path d="M15 6H3v2h12V6zm0 4H3v2h12v-2zM3 16h8v-2H3v2zM17 6v8.18c-.31-.11-.65-.18-1-.18-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3V8h3V6h-5z"/>
                </svg>
                <span>{t('playlist.removeTrack')}</span>
              </button>
            ) : (
              <button
                onClick={() => {
                  setShowShareSubmenu(false);
                  setShowPlaylistSubmenu(!showPlaylistSubmenu);
                }}
                className="w-full px-4 py-2 hover:bg-white/10 text-left flex items-center justify-between gap-3"
              >
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5" fill="white" viewBox="0 0 24 24">
                    <path d="M15 6H3v2h12V6zm0 4H3v2h12v-2zM3 16h8v-2H3v2zM17 6v8.18c-.31-.11-.65-.18-1-.18-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3V8h3V6h-5z"/>
                  </svg>
                  <span>{t('track.addToPlaylist')}</span>
                </div>
                <svg className="w-4 h-4" fill="white" viewBox="0 0 24 24">
                  <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                </svg>
              </button>
            )}

            {/* Télécharger */}
            <button
              onClick={handleDownload}
              className="w-full px-4 py-2 hover:bg-white/10 text-left flex items-center gap-3"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span>{t('download.download') || 'Télécharger'}</span>
              {track?.is_premium && !userIsPremium && user?.status !== 'ADMIN' && (
                <svg 
                  viewBox="0 0 24 24" 
                  fill="currentColor" 
                  className="w-4 h-4 text-yellow-500 ml-auto"
                >
                  <path d="M5 16L3 5L8.5 10L12 4L15.5 10L21 5L19 16H5M19 19C19 19.6 18.6 20 18 20H6C5.4 20 5 19.6 5 19V18H19V19Z"/>
                </svg>
              )}
            </button>

            {/* Partager */}
            <button
              onClick={() => {
                setShowPlaylistSubmenu(false);
                if (isMobile) {
                  handleShare();
                } else {
                  setShowShareSubmenu(!showShareSubmenu);
                }
              }}
              className="w-full px-4 py-2 hover:bg-white/10 text-left flex items-center justify-between gap-3"
            >
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
                <span>{t('share.title') || 'Partager'}</span>
              </div>
              {!isMobile && (
                <svg className="w-4 h-4 rotate-180" fill="white" viewBox="0 0 24 24">
                  <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                </svg>
              )}
            </button>
          </div>

          {/* Sous-menu des playlists (seulement si on n'est pas dans une playlist) */}
          {showPlaylistSubmenu && !isInPlaylist && (
            <div
              ref={submenuRef}
              className="fixed bg-zinc-800 rounded-lg shadow-2xl border border-white/10 py-1 min-w-[200px] max-h-[400px] overflow-y-auto z-50"
              style={{
                top: position.y,
                left: position.x - 210,
              }}
            >
              {/* Playlists sans dossier */}
              {playlistsByFolder[null]?.length > 0 && (
                <div>
                  {playlistsByFolder[null].map(playlist => (
                    <button
                      key={playlist.id}
                      onClick={() => {
                        onAddToPlaylist(track.id, playlist.id);
                        onClose();
                      }}
                      className="w-full px-4 py-2 hover:bg-white/10 text-left flex items-center gap-3"
                    >
                      <svg className="w-5 h-5" fill="white" viewBox="0 0 24 24">
                        <path d="M15 6H3v2h12V6zm0 4H3v2h12v-2zM3 16h8v-2H3v2zM17 6v8.18c-.31-.11-.65-.18-1-.18-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3V8h3V6h-5z"/>
                      </svg>
                      <div className="flex-1 min-w-0">
                        <div className="truncate text-sm">{playlist.name}</div>
                        <div className="text-xs text-white/60">{playlist.track_count || 0} {t('track.tracksCount')}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Playlists par dossier */}
              {folders?.map(folder => {
                const folderPlaylists = playlistsByFolder[folder.id];
                if (!folderPlaylists || folderPlaylists.length === 0) return null;

                return (
                  <div key={folder.id} className="border-t border-white/10">
                    <div className="px-4 py-2 text-xs text-white/60 font-semibold flex items-center gap-2">
                      <svg className="w-4 h-4" fill="white" viewBox="0 0 24 24">
                        <path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z"/>
                      </svg>
                      {folder.name}
                    </div>
                    {folderPlaylists.map(playlist => (
                      <button
                        key={playlist.id}
                        onClick={() => {
                          onAddToPlaylist(track.id, playlist.id);
                          onClose();
                        }}
                        className="w-full px-8 py-2 hover:bg-white/10 text-left flex items-center gap-3"
                      >
                        <svg className="w-4 h-4" fill="white" viewBox="0 0 24 24">
                          <path d="M15 6H3v2h12V6zm0 4H3v2h12v-2zM3 16h8v-2H3v2zM17 6v8.18c-.31-.11-.65-.18-1-.18-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3V8h3V6h-5z"/>
                        </svg>
                        <div className="flex-1 min-w-0">
                          <div className="truncate text-sm">{playlist.name}</div>
                          <div className="text-xs text-white/60">{playlist.track_count || 0} {t('track.tracksCount')}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                );
              })}

              {(!playlists || playlists.length === 0) && (
                <div className="px-4 py-8 text-center text-white/60 text-sm">
                  {t('playlist.noPlaylist')}
                </div>
              )}
            </div>
          )}

          {/* Sous-menu de partage */}
          {showShareSubmenu && !isMobile && (
            <div
              ref={shareSubmenuRef}
              className="fixed bg-zinc-800 rounded-lg shadow-2xl border border-white/10 py-1 min-w-[180px] w-[220px] max-w-[calc(100vw-24px)] z-50"
              style={shareMenuStyle}
            >
              {/* Intégrer */}
              <button
                onClick={handleCopyEmbed}
                className="w-full px-4 py-2 hover:bg-white/10 text-left flex items-center gap-3"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
                <span>{t('share.embed') || 'Intégrer'}</span>
              </button>

              {/* Partager */}
              <button
                onClick={handleShare}
                className="w-full px-4 py-2 hover:bg-white/10 text-left flex items-center gap-3"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                <span>{t('share.share') || 'Partager'}</span>
              </button>
            </div>
          )}
        </>
      )}

      {shareToast && (
        <div className="fixed inset-0 z-[30000] flex items-center justify-center pointer-events-none">
          <div className="px-5 py-3 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold shadow-lg">
            {shareToast}
          </div>
        </div>
      )}
    </>
  );
}
